package info.rezident.app.data.quiz

import kotlinx.serialization.Serializable

/**
 * Domain model for a quiz step
 */
@Serializable
data class QuizStep(
    val id: Int,
    val title: String,
    val subtitle: String
)

/**
 * Domain model for a quiz question
 */
@Serializable
data class QuizQuestion(
    val id: Int,
    val stepId: Int,
    val text: String,
    val predefinedPredefinedAnswers: List<PredefinedAnswer>,
    val openAnswerPlaceholder: String? = null,
    val mandatory: Boolean,
    val search: Boolean,
    val single: Boolean,
    val factor: String? = null,
    val fiveScale: Boolean = false,
    val scaleExplanation: String? = null
)

/**
 * Domain model for a quiz answer
 */
@Serializable
data class PredefinedAnswer(
    val id: Int,
    val text: String
)

/**
 * Data class representing a user's quiz submission
 */
@Serializable
data class QuizUserAnswers(
    val templateId: Int,
    val answers: List<UserAnswer>
)

/**
 * Data class representing a user's answer to a specific question
 */
@Serializable
data class UserAnswer(
    val questionId: Int,
    val answerIds: List<Int>? = null,
    val openAnswer: String? = null,
    val fiveScaleAnswer: Int? = null
)

/**
 * Firestore model for a quiz document
 */
@Serializable
data class QuizUserAnswersModel(
    val templateId: Int? = null,
    val quizId: String? = null,
    val createdAt: String,
    val lastModifiedAt: String,
    val isUnlocked: Boolean = false,
    val lockedRecommendationRanks: List<Int>? = null  // Ranks of the locked recommendation cards (random selection, saved once per quiz)
)

/**
 * Firestore model for an answer document
 */
@Serializable
data class CompletedAnswerModel(
    val questionId: Int,
    val answersIds: List<Int>? = null,
    val openAnswer: String? = null,
    val fiveScaleAnswer: Int? = null
)

/**
 * Internal data class for quiz questions retrieved from Firestore.
 * This is separate from the UI models defined in QuizModels.kt.
 * The UI uses QuizQuestion sealed class, while this repository uses FirestoreQuizQuestion.
 */
@Serializable
data class QuizQuestionModel(
    val id: Int = 0,
    val answers: Map<Int, String> = emptyMap(),
    val mandatory: Boolean = false,
    val question: String = "",
    val openAnswer: String? = null,
    val search: Boolean? = false,
    val single: Boolean = true,
    val stepId: Int = 0,
    val factor: String? = null,
    val fiveScale: Boolean = false,
    val scaleExplanation: String? = null
)

/**
 * Firestore model for recommendations stored in quiz documents
 * Used for both cloud function and legacy AI-generated recommendations
 */
@Serializable
data class RecommendationsModel(
    val createdAt: String = "",
    val recommendations: List<RecommendationItemModel> = emptyList(),
    val incompatibleSpecialties: List<RecommendationItemModel?> = emptyList()
)

/**
 * Firestore model for individual recommendation items
 */
@Serializable
data class RecommendationItemModel(
    val rank: Int = 0,
    val name: String = "",
    val matchScore: Int = 75,
    val pros: List<String>? = null,
    val cons: List<String>? = null,
    // 4 categories from cloud function analysis (stored as question IDs)
    val perfectAnswersHigh: List<String>? = null,
    val perfectAnswersLow: List<String>? = null,
    val differentAnswersHigh: List<String>? = null,
    val differentAnswersLow: List<String>? = null
)

/**
 * Internal data class for quiz steps retrieved from Firestore.
 */
@Serializable
data class QuizStepModel(
    val id: Int = 0,
    val title: String = "",
    val subtitle: String = ""
)

/**
 * Domain model for a completed quiz with document ID and associated data
 */
@Serializable
data class CompletedQuiz(
    val templateId: Int,               // Quiz template ID (currently always 1)
    val quizId: String = "",                    // Firestore document ID
    val createdAt: String,
    val lastModifiedAt: String,
    val answers: List<UserAnswer> = emptyList(),
    val recommendations: RecommendationsResult? = null,
    val isUnlocked: Boolean = false,
    val lockedRecommendationRanks: List<Int> = emptyList()  // Ranks of the locked recommendation cards
)

// Extension functions to convert between Firestore models and domain models

/**
 * Extension function to convert QuizUserAnswersModel to CompletedQuiz domain model
 */
fun QuizUserAnswersModel.toDomainModel(
    documentId: String,
    answers: List<UserAnswer>,
    recommendations: RecommendationsResult? = null
): CompletedQuiz {
    return CompletedQuiz(
        quizId = documentId,
        templateId = this.templateId ?: 1,
        createdAt = this.createdAt,
        lastModifiedAt = this.lastModifiedAt,
        answers = answers,
        recommendations = recommendations,
        isUnlocked = this.isUnlocked,
        lockedRecommendationRanks = this.lockedRecommendationRanks ?: emptyList()
    )
}

/**
 * Extension function to convert RecommendationsModel to RecommendationsResult
 */
fun RecommendationsModel.toDomainModel(): RecommendationsResult {
    return RecommendationsResult(
        recommendations = this.recommendations.map { it.toDomainModel() },
        incompatibleRecommendations = this.incompatibleSpecialties.mapNotNull { it?.toDomainModel() }
            ?: emptyList()
    )
}

/**
 * Extension function to convert RecommendationsResult to RecommendationsModel
 */
fun RecommendationsResult.toFirestoreModel(createdAt: String): RecommendationsModel {
    return RecommendationsModel(
        createdAt = createdAt,
        recommendations = this.recommendations.map { it.toFirestoreModel() },
        incompatibleSpecialties = this.incompatibleRecommendations.map { it.toFirestoreModel() }
    )
}

/**
 * Extension function to convert RecommendationItemModel to RecommendationItem
 */
fun RecommendationItemModel.toDomainModel(): RecommendationItem {
    return RecommendationItem(
        rank = this.rank,
        name = this.name,
        matchScore = this.matchScore,
        pros = this.pros ?: emptyList(),
        cons = this.cons ?: emptyList(),
        // Restore 4 categories from Firestore
        perfectAnswersHigh = this.perfectAnswersHigh ?: emptyList(),
        perfectAnswersLow = this.perfectAnswersLow ?: emptyList(),
        differentAnswersHigh = this.differentAnswersHigh ?: emptyList(),
        differentAnswersLow = this.differentAnswersLow ?: emptyList(),
        characteristics = SpecialtyMetadataRepository.getCharacteristics(this.name)
    )
}

/**
 * Extension function to convert RecommendationItem to RecommendationItemModel
 * Only includes pros/cons and answer categories if they are not empty (null means field won't be written to Firestore)
 */
fun RecommendationItem.toFirestoreModel(): RecommendationItemModel {
    return RecommendationItemModel(
        rank = this.rank,
        name = this.name,
        matchScore = this.matchScore,
        pros = if (this.pros.isNotEmpty()) this.pros else null,
        cons = if (this.cons.isNotEmpty()) this.cons else null,
        // Persist 4 categories to Firestore (only if not empty)
        perfectAnswersHigh = if (this.perfectAnswersHigh.isNotEmpty()) this.perfectAnswersHigh else null,
        perfectAnswersLow = if (this.perfectAnswersLow.isNotEmpty()) this.perfectAnswersLow else null,
        differentAnswersHigh = if (this.differentAnswersHigh.isNotEmpty()) this.differentAnswersHigh else null,
        differentAnswersLow = if (this.differentAnswersLow.isNotEmpty()) this.differentAnswersLow else null
    )
}